// ============================================================================
// RECOVERY OFFICER DASHBOARD — Phase 8.D
// ============================================================================
// Landing screen for recovery officers. Four read-only widgets sourced from
// /api/v1/RecoveryDashboard endpoints; each widget owns its own hook. The
// Refresh button in the toolbar bumps a shared refreshKey that every widget
// re-fetches off, so the user sees a coordinated update.
//
// Scope: server always filters by the authenticated user
// (assigned_agent_user_id). No officer dropdown today — supervisor view can
// be added later behind a `recovery-dashboard.view-all` action.

import React, { useCallback, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';

import MyWorkloadWidget from './widgets/MyWorkloadWidget';
import UnclaimedQueueWidget from './widgets/UnclaimedQueueWidget';
import ActionTodayWidget from './widgets/ActionTodayWidget';
import RecentClosuresWidget from './widgets/RecentClosuresWidget';

import './dashboard.css';

interface RecoveryOfficerDashboardScreenProps {
  tabId?: string;
  moduleName?: string;
}

const RecoveryOfficerDashboardScreen: React.FC<RecoveryOfficerDashboardScreenProps> = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const handleRefresh = useCallback(() => setRefreshKey(k => k + 1), []);

  const actionButtons: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={16} />,
      label: 'Refresh',
      onClick: handleRefresh,
      variant: 'icon',
    },
  ];

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Recovery Officer Dashboard"
        description="Your open workload, the queue to pick from, what needs action today, and recent closures."
        icon={<IconSystem.dashboard size={18} />}
        actions={actionButtons}
      />
      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0, padding: '0.75rem' }}>
          <div className="dashboard-grid">
            <MyWorkloadWidget       refreshKey={refreshKey} />
            <UnclaimedQueueWidget   refreshKey={refreshKey} />
            <ActionTodayWidget      refreshKey={refreshKey} />
            <RecentClosuresWidget   refreshKey={refreshKey} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecoveryOfficerDashboardScreen;
